import React, { useEffect, useMemo, useState } from 'react';
import { useId } from '@uifabric/react-hooks';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import propTypes from 'prop-types';
import { debounce } from 'lodash';
import { Icon } from 'office-ui-fabric-react';
import styles, { buttonStyles, textBoxStyles } from './TypeaheadPicker.styles';
import AutosuggestDropdown from '../AutosuggestDropdown';
import { dropDownButtonTestId, textFieldTestId } from './testUtils';

const getOption = (key, options) => options.find(o => o.key === key);

const BasicTypeaheadPicker = ({
  options = [],
  selectedValue = null,
  onChange = () => {},
  onType = () => {},
  isLoading = false,
  error = null,
  placeholder = '',
  includePlaceholderOption = false,
  displayIconInTextBox = false,
  showDropdown = false,
  resultFilter = undefined,
  prefixIcon = undefined,
  textFieldProps = {},
  disabled = false,
  onDropdownClick = () => {},
  onDropdownDismiss = () => {},
  headerOrder = [],
  dropDownClassName = styles.dropdownContainer,
  showDropdownChevron = true,
  dataTestId = textFieldTestId,
}) => {
  const id = useId();
  const [filter, setFilter] = useState('');
  const [textFieldValue, setTextFieldValue] = useState('');
  useEffect(() => {
    setTextFieldValue(selectedValue?.text || '');
  }, [selectedValue]);
  const searchRegex = RegExp(filter.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&'), 'i');
  const debouncedOnType = useMemo(
    () => debounce(onType, 500, { leading: true, tailing: false }),
    [onType],
  );

  const onSearchClick = () => {
    setTextFieldValue('');
  };

  const itemOnClick = ({ value }) => {
    const selected = getOption(value, options);
    setTextFieldValue(selected?.text);
    onChange(selected);
    onDropdownDismiss();
  };

  const onSearchChange = (event, value) => {
    setFilter(value);
    setTextFieldValue(value);
    debouncedOnType(value);
  };

  const dropOnDismiss = () => {
    onDropdownDismiss();
    setTextFieldValue(selectedValue?.text);
  };

  const dropDownButtonOnClick = () => {
    setFilter('');
    onDropdownClick();
  };

  const optionListMapper = option => ({
    ...option,
    value: option.key,
    label: option.text,
    header: option.header !== undefined ? option.header : '',
  });

  // if we show the placeholder option we start the list with the placeholder (with value of null)
  const displayedOptions = (
    includePlaceholderOption ? [{ key: null, text: placeholder, header: '' }] : []
  )
    .concat(options)
    .filter(resultFilter || (o => searchRegex.test(o.text)))
    .map(optionListMapper);

  const renderPrefix = () =>
    prefixIcon ? (
      <Icon iconName={prefixIcon} />
    ) : (
      displayIconInTextBox && selectedValue?.icon && <Icon iconName={selectedValue?.icon} />
    );

  return (
    <>
      <div className={styles.root}>
        <TextField
          onBlur={() => {
            setTextFieldValue(selectedValue?.text || '');
          }}
          id={id}
          className={styles.textBox}
          styles={textBoxStyles}
          value={textFieldValue}
          onChange={onSearchChange}
          onClick={onSearchClick}
          autoComplete="off"
          data-testid={dataTestId}
          placeholder={placeholder}
          onRenderPrefix={renderPrefix}
          disabled={disabled}
          onRenderSuffix={() =>
            showDropdownChevron && (
              <IconButton
                styles={buttonStyles}
                iconProps={{ iconName: 'ChevronDown' }}
                aria-labelledby={id}
                onClick={dropDownButtonOnClick}
                data-testid={dropDownButtonTestId}
              />
            )
          }
          {...textFieldProps}
        />
        {!disabled && showDropdown && (
          <AutosuggestDropdown
            suggestions={displayedOptions}
            target={id}
            calloutOnDismiss={dropOnDismiss}
            onClick={itemOnClick}
            isLoading={isLoading}
            error={error}
            headerOrder={headerOrder}
            className={dropDownClassName}
          />
        )}
      </div>
    </>
  );
};

BasicTypeaheadPicker.propTypes = {
  options: propTypes.arrayOf(
    propTypes.shape({
      key: propTypes.any.isRequired,
      text: propTypes.string.isRequired,
    }),
  ),
  selectedValue: propTypes.shape({
    key: propTypes.any.isRequired,
    text: propTypes.string.isRequired,
  }),
  onChange: propTypes.func,
  onType: propTypes.func,
  isLoading: propTypes.bool,
  placeholder: propTypes.string,
  includePlaceholderOption: propTypes.bool,
  error: propTypes.string,
  displayIconInTextBox: propTypes.bool,
  showDropdown: propTypes.bool,
  onDropdownClick: propTypes.func,
  onDropdownDismiss: propTypes.func,
  headerOrder: propTypes.arrayOf(propTypes.string),
  dropDownClassName: propTypes.string,
  noDropdownButton: propTypes.bool,
  resultFilter: propTypes.func,
  prefixIcon: propTypes.string,
  textFieldProps: propTypes.object,
  disabled: propTypes.bool,
  showDropdownChevron: propTypes.bool,
  dataTestId: propTypes.string,
};

BasicTypeaheadPicker.defaultProps = {
  options: [],
  selectedValue: null,
  onType: () => {},
  onChange: () => {},
  isLoading: false,
  placeholder: '',
  includePlaceholderOption: false,
  error: null,
  displayIconInTextBox: false,
  showDropdown: false,
  onDropdownClick: () => {},
  onDropdownDismiss: () => {},
  headerOrder: [],
  dropDownClassName: styles.dropdownContainer,
  noDropdownButton: false,
  resultFilter: undefined,
  prefixIcon: undefined,
  textFieldProps: {},
  disabled: false,
  showDropdownChevron: true,
  dataTestId: textFieldTestId,
};

export default BasicTypeaheadPicker;
